/**
 * Location Assignment Column Definitions
 *
 * Column definitions for the AdminLocationAssignment DataTable.
 * Provides cell renderers used by both flat and grouped views.
 */

import { type ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import type { AdminLocationAssignment } from "@/services/adminService";
import { getLevelLabel, getLevelIcon, getLevelColor, formatPrivilege } from "../utils/adminLevelUtils";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";

function formatDate(dateString: string): string {
  try {
    return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: idLocale });
  } catch {
    return "-";
  }
}

interface LocationAssignmentColumnCallbacks {
  onEdit?: (item: AdminLocationAssignment) => void;
  onDelete?: (item: AdminLocationAssignment) => void;
}

export function createLocationAssignmentColumns({
  onEdit,
  onDelete,
}: LocationAssignmentColumnCallbacks): ColumnDef<AdminLocationAssignment>[] {
  return [
    {
      id: "admin",
      accessorFn: (row) => row.admin?.nama_lengkap ?? "",
      header: "Admin",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-semibold">{row.original.admin?.nama_lengkap ?? "-"}</span>
          <span className="text-xs text-muted-foreground font-mono">
            {row.original.admin?.callsign ?? "-"}
          </span>
        </div>
      ),
      enableSorting: true,
      size: 200,
    },
    {
      accessorKey: "level",
      header: "Level",
      cell: ({ row }) => {
        const level = row.original.level;
        const Icon = getLevelIcon(level);
        return (
          <Badge
            variant="outline"
            className={cn("flex items-center gap-1 w-fit", getLevelColor(level))}
          >
            <Icon className="h-3 w-3" />
            {getLevelLabel(level)}
          </Badge>
        );
      },
      enableSorting: true,
      size: 150,
    },
    {
      id: "provinces",
      accessorFn: (row) => row.provinces?.join(", ") ?? "",
      header: "Provinsi",
      cell: ({ row }) => {
        const provinces = row.original.provinces;
        if (!provinces || provinces.length === 0) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {provinces.map((p) => (
              <Badge key={p} variant="secondary" className="text-xs">
                {p}
              </Badge>
            ))}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      id: "cities",
      accessorFn: (row) => row.cities?.join(", ") ?? "",
      header: "Kota/Kabupaten",
      cell: ({ row }) => {
        const cities = row.original.cities;
        if (!cities || cities.length === 0) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {cities.map((c) => (
              <Badge key={c} variant="secondary" className="text-xs">
                {c}
              </Badge>
            ))}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      id: "privileges",
      accessorFn: (row) => row.privileges?.join(", ") ?? "",
      header: "Hak Akses",
      cell: ({ row }) => {
        const privileges = row.original.privileges;
        if (!privileges || privileges.length === 0) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {privileges.map((p) => (
              <Badge key={p} variant="outline" className="text-xs">
                {formatPrivilege(p)}
              </Badge>
            ))}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={row.original.isActive ? "default" : "secondary"}
          className={cn(
            row.original.isActive
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
          )}
        >
          {row.original.isActive ? "Aktif" : "Tidak Aktif"}
        </Badge>
      ),
      enableSorting: false,
      size: 100,
    },
    {
      id: "createdAt",
      accessorFn: (row) => row.createdAt,
      header: "Dibuat",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(row.original.createdAt)}
        </span>
      ),
      enableSorting: true,
      size: 180,
    },
    {
      id: "actions",
      header: () => <div className="text-right">Aksi</div>,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(row.original)}
              className="h-8 w-8 p-0"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(row.original)}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
      enableSorting: false,
      size: 120,
    },
  ];
}

// Province-grouped data helpers for the special grouped view

export function getPrimaryProvince(a: AdminLocationAssignment): string {
  if (a.level === "nasional") return "NASIONAL";
  return (a.provinces?.[0] ?? "-").toString().trim() || "-";
}

export function provinceRank(p: string): number {
  return String(p).toUpperCase() === "NASIONAL" ? 0 : 1;
}

export function levelRank(level: string): number {
  if (level === "province") return 1;
  if (level === "city") return 2;
  return 0;
}

export function sortAssignments(
  assignments: AdminLocationAssignment[],
  sortBy: string,
  sortOrder: "asc" | "desc"
): AdminLocationAssignment[] {
  const sorted = [...assignments];
  sorted.sort((a, b) => {
    const order = sortOrder === "asc" ? 1 : -1;

    if (sortBy === "province" || sortBy === "provinces") {
      const ap = getPrimaryProvince(a).toLowerCase();
      const bp = getPrimaryProvince(b).toLowerCase();
      const arank = provinceRank(ap);
      const brank = provinceRank(bp);
      if (arank !== brank) return arank < brank ? -1 : 1;
      if (ap !== bp) return ap < bp ? -1 * order : 1 * order;

      const ar = levelRank(a.level);
      const br = levelRank(b.level);
      if (ar !== br) return ar < br ? -1 : 1;

      const ac = (a.cities?.[0] ?? "").toLowerCase();
      const bc = (b.cities?.[0] ?? "").toLowerCase();
      if (ac !== bc) return ac < bc ? -1 : 1;

      const an = (a.admin?.nama_lengkap ?? "").toLowerCase();
      const bn = (b.admin?.nama_lengkap ?? "").toLowerCase();
      if (an !== bn) return an < bn ? -1 : 1;

      const at = new Date(a.createdAt).getTime();
      const bt = new Date(b.createdAt).getTime();
      return at < bt ? 1 : at > bt ? -1 : 0;
    }

    let aVal: any;
    let bVal: any;

    if (sortBy === "admin") {
      aVal = (a.admin?.nama_lengkap ?? "").toLowerCase();
      bVal = (b.admin?.nama_lengkap ?? "").toLowerCase();
    } else if (sortBy === "level") {
      aVal = a.level;
      bVal = b.level;
    } else {
      aVal = new Date(a.createdAt).getTime();
      bVal = new Date(b.createdAt).getTime();
    }

    if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });
  return sorted;
}

export interface ProvinceGroup {
  province: string;
  parents: AdminLocationAssignment[];
  children: AdminLocationAssignment[];
}

export function groupByProvince(sorted: AdminLocationAssignment[]): ProvinceGroup[] {
  const groups = new Map<string, AdminLocationAssignment[]>();
  for (const a of sorted) {
    const key = getPrimaryProvince(a);
    const list = groups.get(key) ?? [];
    list.push(a);
    groups.set(key, list);
  }

  const entries = Array.from(groups.entries()).sort((a, b) => {
    const ar = provinceRank(a[0]);
    const br = provinceRank(b[0]);
    if (ar !== br) return ar < br ? -1 : 1;
    return a[0].localeCompare(b[0]);
  });

  return entries.map(([province, list]) => ({
    province,
    parents: list.filter((x) => x.level === "province" || x.level === "nasional"),
    children: list.filter((x) => x.level === "city"),
  }));
}
